import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import 'moment/locale/fr';
import 'moment/locale/de';
import 'moment/locale/nb';
import DropdownOptions from 'commons/src/components/dropdown/DropdownOptions';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { RequiredRoleLevel } from 'commons/src/models/commonEnums';
import { MenuItem } from 'commons/src/models/menuModels';
import { SetNumberOfTilesInWidth, setNumberOfTilesInWidth } from '../../actions';
import { minScreenWidthNumberOfTiles } from '../../constants';
import calcTextSize, { getParentSize } from '../../features/publicDashboard/commonFunctions';
import { PublicDashboardProps } from '../../models/commons';
import { Store } from '../../reducers';

type StateProps = {
    dashboardProps?: PublicDashboardProps;
    numberOfTilesInWidth: number;
};

type ActionProps = {
    setWidth: (width: number) => void;
};

export type Props = StateProps & ActionProps;

export const PublicDashboardPageHeaderComponent = (props: Props): React.ReactElement => {
    const { dashboardProps, numberOfTilesInWidth, setWidth } = props;
    const { t: txt } = useTranslation();
    const [sensorValueFontSize, setSensorValueFontSize] = useState('26px');
    const [fontScaled, setFontScaled] = useState(false);

    const updateFontSize = (fontSize: string): void => {
        setFontScaled(true);
        setSensorValueFontSize(fontSize);
    };

    const calcText = (): void => {
        const text = document.getElementById('headerText');
        if (text) {
            const { parentWidth, parentHeight, textHeight } = getParentSize(text);
            const parentContainerHeight = parentHeight * 0.6;
            const maxTextGrowth = parentContainerHeight / textHeight;
            calcTextSize(text, parentWidth, updateFontSize, maxTextGrowth);
        }
    };

    useEffect((): void => {
        if (!fontScaled) {
            calcText();
        }
    }, []);

    const selectWidth = (width: number): void => {
        setWidth(width);
    };

    const getTilesMaxWidth = (): number => {
        const windowSize = document.documentElement.clientWidth;
        const recommendedWindowSizeLessThan = Object.keys(minScreenWidthNumberOfTiles).find(
            widthOption =>
                minScreenWidthNumberOfTiles[widthOption as keyof typeof minScreenWidthNumberOfTiles] > windowSize
        );
        let recommendedWindowSize = 4;
        if (recommendedWindowSizeLessThan) {
            const recommendedAsNumber = parseInt(recommendedWindowSizeLessThan, 10);
            recommendedWindowSize = recommendedAsNumber > 1 ? recommendedAsNumber - 1 : 1;
        }
        if (numberOfTilesInWidth > recommendedWindowSize) {
            setWidth(recommendedWindowSize);
        }
        return recommendedWindowSize;
    };
    const [recommendedWindowSize, setRecommendedWindowSize] = useState(getTilesMaxWidth());
    const calculateTileWidthOptions = () => {
        const maxTileWidth = getTilesMaxWidth();
        setRecommendedWindowSize(maxTileWidth);
    };

    const numberOfTilesInWidthOptions: MenuItem[] = [
        {
            onClick: (): void => selectWidth(1),
            text: 'PublicDashboardWidth.OneTile',
            selector: true,
            isSelected: numberOfTilesInWidth === 1,
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            disabled: false,
            requiredGroupTypes: [],
        },
        {
            onClick: (): void => selectWidth(2),
            text: 'PublicDashboardWidth.TwoTiles',
            selector: true,
            isSelected: numberOfTilesInWidth === 2,
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            disabled: recommendedWindowSize < 2,
            requiredGroupTypes: [],
        },
        {
            onClick: (): void => selectWidth(3),
            text: 'PublicDashboardWidth.ThreeTiles',
            selector: true,
            isSelected: numberOfTilesInWidth === 3,
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            disabled: recommendedWindowSize < 3,
            requiredGroupTypes: [],
        },
        {
            onClick: (): void => selectWidth(4),
            text: 'PublicDashboardWidth.FourTiles',
            selector: true,
            isSelected: numberOfTilesInWidth === 4,
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            disabled: recommendedWindowSize < 4,
            requiredGroupTypes: [],
        },
    ];

    useEffect((): (() => void) => {
        window.addEventListener('resize', calculateTileWidthOptions);
        return (): void => window.removeEventListener('resize', calculateTileWidthOptions);
    }, []);

    return (
        <div className="public-dashboard__page-header">
            <div className="public-dashboard__page-header__content">
                {dashboardProps?.organizationLogo && (
                    <img
                        className="public-dashboard__page-header__logo"
                        src={dashboardProps.organizationLogo}
                        alt={txt('OrganizationProfile.LogoImage')}
                    />
                )}
                <div className="public-dashboard__page-header__content__text-wrapper">
                    <div
                        id="headerText"
                        className="public-dashboard__page-header__name"
                        style={{ fontSize: sensorValueFontSize }}
                    >
                        {dashboardProps?.dashboardName}
                    </div>
                    <div id="OrgName" className="public-dashboard__page-header__org">
                        {dashboardProps?.organizationName}
                    </div>
                </div>
            </div>
            <DropdownOptions
                options={numberOfTilesInWidthOptions}
                id="public-header-selector"
                dotMenu
                customButton={
                    <MaterialIcon extraClass="public-dashboard__page-header__menu-icon" name="aspect_ratio" />
                }
            />
        </div>
    );
};

const mapStateToProps = (state: Store): StateProps => {
    const {
        publicDashboard: { dashboardProps },
        publicUserPreferences: { numberOfTilesInWidth },
    } = state;
    return {
        dashboardProps,
        numberOfTilesInWidth,
    };
};
const mapDispatchToProps = (dispatch: Dispatch): ActionProps => ({
    setWidth: (width: number): SetNumberOfTilesInWidth => dispatch(setNumberOfTilesInWidth(width)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PublicDashboardPageHeaderComponent);
